import { useState } from 'react';
import { t } from '@/lib/i18n';
import { Icon } from '@/lib/icon';
import { RunJobSheet } from '@/features/jobs/components/RunJobSheet';
import { useRecommendedJobs } from '../hooks';
import styles from './RecommendedJobsCard.module.css';

interface Props {
  memberId: string;
  /** Pre-assigns this member when the user clicks a recommendation. */
  preassignMemberId: string;
}

export function RecommendedJobsCard({ memberId, preassignMemberId }: Props) {
  const { data, isLoading, isError } = useRecommendedJobs(memberId);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  function handleClick(templateId: string) {
    setSelectedTemplateId(templateId);
    setSheetOpen(true);
  }

  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>{t('Recommended jobs')}</h2>
        <div className={styles.sectionCard}>
          {isLoading ? (
            <p className={styles.muted}>{t('Loading…')}</p>
          ) : isError ? (
            <p className={styles.muted}>{t("Couldn't load recommendations.")}</p>
          ) : !data || data.items.length === 0 ? (
            <p className={styles.muted}>
              {t(
                'No matching jobs yet — assign positions or units to surface relevant work from your library.',
              )}
            </p>
          ) : (
            <ul className={styles.list}>
              {data.items.map((j, idx) => (
                <li key={j.id}>
                  <button type="button" className={styles.row} onClick={() => handleClick(j.id)}>
                    <span className={styles.icon} aria-hidden="true">
                      <Icon iconRef={j.icon} size={20} />
                    </span>
                    <span className={styles.body}>
                      <span className={styles.titleLine}>
                        <span className={styles.title}>{j.title}</span>
                        {idx === 0 ? <span className={styles.badge}>{t('Best match')}</span> : null}
                      </span>
                      {j.description ? (
                        <span className={styles.description}>{j.description}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {selectedTemplateId !== null && (
        <RunJobSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          templateId={selectedTemplateId}
          prefillMemberId={preassignMemberId}
        />
      )}
    </>
  );
}
